import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConstraintPriorities, ConstraintsItemType } from '../components/Constraints/types';
import { ConstraintFieldRange } from './useTeacherConstraints';

/**
 * Flat subject constraints blob written on the subject↔version edge. Kept flat because the Timefold
 * export reads these exact keys (subjectConstraints → Lesson.maxPerDay/maxDaysPerWeek/preferEdgeOfDay).
 * maxPerDay (F8) / maxDaysPerWeek (F6) carry a per-subject priority → solver tier; edgeOfDay (F15)
 * is a boolean preference scored at a fixed global weight, so it has no priority.
 */
export type SubjectConstraints = {
  maxPerDay?: number;
  maxPerDayPriority?: ConstraintPriorities;
  maxDaysPerWeek?: number;
  maxDaysPerWeekPriority?: ConstraintPriorities;
  edgeOfDay?: boolean;
  edgeOfDayPriority?: ConstraintPriorities;
};

export type SubjectFieldDescriptor = {
  /** Numeric field on the blob (also the key into {@link subjectConstraintLabels}). */
  field: 'maxPerDay' | 'maxDaysPerWeek';
  priorityField: 'maxPerDayPriority' | 'maxDaysPerWeekPriority';
  range: ConstraintFieldRange;
};

/** Single source of truth for the subject number fields: drives both the list and the form. */
export const subjectConstraintFields: SubjectFieldDescriptor[] = [
  { field: 'maxPerDay', priorityField: 'maxPerDayPriority', range: 'posPerDay' },
  { field: 'maxDaysPerWeek', priorityField: 'maxDaysPerWeekPriority', range: 'perWeek' },
];

/**
 * Display labels + tooltips for the subject conditions, keyed by field. Literal `t('...')` calls (not
 * `t(labelKey)`) so the i18next-parser extracts them — no dynamic keys, no manifest entry.
 * Shared by the summary list ({@link useSubjectConstraints}) and the form.
 */
export const subjectConstraintLabels = (t: TFunction): Record<string, { label: string; tooltip: string }> => ({
  maxPerDay: {
    label: t('conditions.subject.maxPerDay'),
    tooltip: t('conditions.subject.maxPerDayTooltip'),
  },
  maxDaysPerWeek: {
    label: t('conditions.subject.maxDaysPerWeek'),
    tooltip: t('conditions.subject.maxDaysPerWeekTooltip'),
  },
});

/** Builds the displayed constraint list from a subject blob (shared {@link ConstraintsItemType}). */
export const useSubjectConstraints = (constraints: SubjectConstraints): ConstraintsItemType[] => {
  const { t } = useTranslation();
  return useMemo(() => {
    const labels = subjectConstraintLabels(t);
    const items: ConstraintsItemType[] = [];
    for (const descriptor of subjectConstraintFields) {
      const value = constraints[descriptor.field];
      if (value == null) continue;
      items.push({
        type: descriptor.field,
        priority: constraints[descriptor.priorityField] ?? null,
        text: `${labels[descriptor.field].label}: ${value}`,
      });
    }
    if (constraints.edgeOfDay) {
      items.push({
        type: 'edgeOfDay',
        priority: constraints.edgeOfDayPriority ?? null,
        text: t('conditions.subject.edgeOfDay'),
      });
    }
    return items;
  }, [t, constraints]);
};
